import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import i18n, { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import {
  DURATION_BUCKETS,
  PRICE_BUCKETS,
  TRIP_TYPE_LABELS,
  WIZARD_VARIANTS,
  WIZARD_VARIANT_KEYS,
  type WizardVariant,
} from "@ckgo2/shared";
import type { TripType } from "@ckgo2/shared/types";
import {
  ClipboardList,
  MapPin,
  MailCheck,
  Trash2,
  TrendingUp,
  BarChart2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface QuizResult {
  id: string;
  variant: WizardVariant;
  answers: Record<string, string>;
  /** Názvy zobrazených výsledků; `null` = výsledek už neexistuje. */
  recommendedNames: Array<string | null>;
  clickedIds: string[];
  email?: string;
  subscribedNewsletter: boolean;
  createdAt: string;
}

interface QuizStats {
  totalRuns: number;
  newsletterSignups: number;
  signupRate: number;
  last30Days: number;
  topResults: Array<{ id: string; name: string; recommendations: number; clicks: number }>;
  answerDistribution: Record<string, Record<string, number>>;
}

interface PaginatedResponse {
  data: QuizResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const monthLabel = (month: number) =>
  new Date(2000, month - 1, 1).toLocaleString(uiLocale(), { month: "long" });

// Popisky odpovědí ze sdílených zdrojů, aby se admin nerozešel s průvodcem. Nové klíče
// (průvodce cestou, řez 10) i STARÉ (`type/budget/companion/region` — záznamy kvízu
// před řezem 10), aby se starší běhy vypsaly čitelně.
const ANSWER_LABELS: Record<string, Record<string, string>> = {
  tripType: Object.fromEntries(
    (Object.keys(TRIP_TYPE_LABELS) as TripType[]).map((k) => [k, enumLabel(TRIP_TYPE_LABELS, k)]),
  ),
  bestMonth: Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [String(i + 1), monthLabel(i + 1)]),
  ),
  type: {
    adventure: i18n.t("content.quiz.answers.adventure"),
    relax: i18n.t("content.quiz.answers.relax"),
    culture: i18n.t("content.quiz.answers.culture"),
    nature: i18n.t("content.quiz.answers.nature"),
  },
  budget: Object.fromEntries(PRICE_BUCKETS.map((b) => [b.key, b.label])),
  duration: Object.fromEntries(DURATION_BUCKETS.map((b) => [b.key, b.label])),
  companion: {
    solo: i18n.t("content.quiz.answers.solo"),
    couple: i18n.t("content.quiz.answers.couple"),
    family: i18n.t("content.quiz.answers.family"),
    friends: i18n.t("content.quiz.answers.friends"),
  },
  region: {
    northern: i18n.t("content.quiz.answers.northern"),
    central: i18n.t("content.quiz.answers.central"),
    southern: i18n.t("content.quiz.answers.southern"),
    combination: i18n.t("content.quiz.answers.combination"),
  },
};

export default function QuizResultsPage() {
  const { t } = useTranslation();
  const [variant, setVariant] = useState<WizardVariant>("kviz");
  const variantConfig = WIZARD_VARIANTS[variant];
  const showsTrips = variantConfig.resultKind === "trip";
  const variantLabels: Record<WizardVariant, string> = {
    kviz: t("content.quiz.variantKviz"),
    hledac: t("content.quiz.variantHledac"),
  };
  const [page, setPage] = useState(1);
  const [showStats, setShowStats] = useState(true);
  const queryClient = useQueryClient();

  const { data: resultsData, isLoading: resultsLoading } = useQuery({
    queryKey: ["quiz-results", variant, page],
    queryFn: () =>
      api.get<ApiResponse<QuizResult[]> & { pagination: PaginatedResponse["pagination"] }>(
        `/api/admin/quiz/results?variant=${variant}&page=${page}&limit=20`,
      ),
  });

  const { data: statsData } = useQuery({
    queryKey: ["quiz-stats", variant],
    queryFn: () => api.get<ApiResponse<QuizStats>>(`/api/admin/quiz/stats?variant=${variant}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/quiz/results/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz-results"] });
      queryClient.invalidateQueries({ queryKey: ["quiz-stats"] });
    },
  });

  const results = resultsData?.data || [];
  const pagination = resultsData?.pagination;
  const stats = statsData?.data;

  const handleDelete = (id: string) => {
    if (confirm(t("content.quiz.confirmDelete"))) {
      deleteMutation.mutate(id);
    }
  };

  const questionLabels: Record<string, string> = {
    tripType: t("content.quiz.questions.tripType"),
    duration: t("content.quiz.questions.duration"),
    bestMonth: t("content.quiz.questions.bestMonth"),
    type: t("content.quiz.questions.type"),
    budget: t("content.quiz.questions.budget"),
    companion: t("content.quiz.questions.companion"),
    region: t("content.quiz.questions.region"),
  };

  const getAnswerLabel = (question: string, answer: string) => {
    return ANSWER_LABELS[question]?.[answer] || answer;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.quiz.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("content.quiz.subtitle")}</p>
        </div>
        <button onClick={() => setShowStats(!showStats)} className="btn-secondary">
          <BarChart2 className="h-5 w-5" />
          {showStats ? t("content.quiz.hideStats") : t("content.quiz.showStats")}
        </button>
      </div>

      {/* Variant switcher — kvíz doporučuje zájezdy, hledač země; čísla se nesčítají. */}
      <div
        role="tablist"
        aria-label={t("content.quiz.variantSwitch")}
        className="inline-flex rounded-lg border border-gray-200 p-1 dark:border-gray-700"
      >
        {WIZARD_VARIANT_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={variant === key}
            onClick={() => {
              setVariant(key);
              setPage(1);
            }}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              variant === key
                ? "bg-primary-600 text-white"
                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            }`}
          >
            {variantLabels[key]}
          </button>
        ))}
      </div>

      {/* Stats */}
      {showStats && stats && (
        <div className="space-y-6">
          {/* Quick stats */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-primary-100 dark:bg-primary-900/30 rounded-full p-2">
                  <ClipboardList className="text-primary-600 dark:text-primary-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("content.quiz.totalQuizzes")}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.totalRuns}
                  </p>
                </div>
              </div>
            </div>
            {variantConfig.offersNewsletter && (
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="bg-success-100 dark:bg-success-900/30 rounded-full p-2">
                    <MailCheck className="text-success-600 dark:text-success-400 h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("content.quiz.newsletterSignups")}
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {stats.newsletterSignups} ({stats.signupRate}%)
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-accent-100 dark:bg-accent-900/30 rounded-full p-2">
                  <TrendingUp className="text-accent-600 dark:text-accent-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("content.quiz.last30Days")}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.last30Days}
                  </p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="bg-warning-100 dark:bg-warning-900/30 rounded-full p-2">
                  <MapPin className="text-warning-600 dark:text-warning-400 h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {showsTrips ? t("content.quiz.topTrip") : t("content.quiz.topDestination")}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stats.topResults[0]?.name || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Top results — „Doporučeno" (zobrazení ve výsledku) a „Prokliky" (kliknutí na
              výsledek) jsou dvě různá čísla vedle sebe, žádný poměr (P24). */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {showsTrips
                ? t("content.quiz.mostRecommended")
                : t("content.quiz.mostRecommendedDestinations")}
            </h2>
            {stats.topResults.length === 0 ? (
              <p className="text-sm text-gray-500">-</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4 font-medium">#</th>
                    <th className="py-2 pr-4 font-medium">{t("content.quiz.thResult")}</th>
                    <th className="py-2 pr-4 text-right font-medium">
                      {t("content.quiz.thRecommended")}
                    </th>
                    <th className="py-2 text-right font-medium">{t("content.quiz.thClicks")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {stats.topResults.map((item, index) => (
                    <tr key={item.id}>
                      <td className="py-2 pr-4 text-gray-500">{index + 1}</td>
                      <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">
                        {item.name}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{item.recommendations}</td>
                      <td className="py-2 text-right tabular-nums">{item.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Answer distribution */}
          {Object.keys(stats.answerDistribution).length > 0 && (
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {t("content.quiz.answerDistribution")}
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {Object.entries(stats.answerDistribution).map(([question, counts]) => (
                  <div key={question}>
                    <h3 className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                      {questionLabels[question] ?? question}
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {Object.entries(counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([answer, n]) => (
                          <li key={answer} className="flex justify-between gap-2">
                            <span className="text-gray-900 dark:text-white">
                              {getAnswerLabel(question, answer)}
                            </span>
                            <span className="tabular-nums text-gray-500">{n}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      <div className="card overflow-hidden p-0">
        {resultsLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : results.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <ClipboardList className="mb-2 h-12 w-12 text-gray-300" />
            <p>{t("content.quiz.empty")}</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.date")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("content.quiz.thAnswers")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("content.quiz.thRecommended")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.email")}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {results.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <span className="text-gray-900 dark:text-white">
                        {new Date(result.createdAt).toLocaleDateString(uiLocale())}
                      </span>
                      <br />
                      <span className="text-sm text-gray-500">
                        {new Date(result.createdAt).toLocaleTimeString(uiLocale(), {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(result.answers).map(([q, a]) => (
                          <span
                            key={q}
                            className="badge bg-gray-100 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            title={questionLabels[q] ?? q}
                          >
                            {getAnswerLabel(q, a)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {result.recommendedNames.slice(0, 3).map((name, i) => (
                          <span key={i} className="badge-primary text-xs">
                            {name ?? t("content.quiz.resultUnavailable")}
                          </span>
                        ))}
                        {result.recommendedNames.length > 3 && (
                          <span className="badge bg-gray-100 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            +{result.recommendedNames.length - 3}
                          </span>
                        )}
                        {result.clickedIds.length > 0 && (
                          <span className="badge bg-gray-100 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                            {t("content.quiz.clicksCount", { count: result.clickedIds.length })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {result.email ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={`mailto:${result.email}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {result.email}
                          </a>
                          {result.subscribedNewsletter && (
                            <span title={t("content.quiz.subscribedNewsletter")}>
                              <MailCheck className="text-success-500 h-4 w-4" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          title={t("common.actions.delete")}
                          aria-label={t("common.actions.delete")}
                          onClick={() => handleDelete(result.id)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("content.quiz.showing", {
                    from: (pagination.page - 1) * pagination.limit + 1,
                    to: Math.min(pagination.page * pagination.limit, pagination.total),
                    total: pagination.total,
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="btn-ghost btn-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t("common.pagination.previous")}
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.totalPages}
                    className="btn-ghost btn-sm"
                  >
                    {t("common.pagination.next")}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
